import React, { useState, useEffect, MouseEvent, ReactNode } from "react";
import {
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  ListItemIcon,
  Divider,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
  TextField,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  GridOn as GridOnIcon,
  LocationOn as LocationOnIcon,
  Factory as FactoryIcon,
  Apartment as ApartmentIcon,
  AttachMoney as AttachMoneyIcon,
  Diversity3 as Diversity3Icon,
  ConnectWithoutContact as ConnectWithoutContactIcon,
  Grading as GradingIcon,
  DeviceHub as DeviceHubIcon,
  CalendarMonth as CalendarMonthIcon,
  LocalPhone as LocalPhoneIcon,
  DomainAdd as DomainAddIcon,
  TravelExplore as TravelExploreIcon,
  FindInPage as FindInPageIcon,
  ContentPasteOff as ContentPasteOffIcon,
  TextFields as TextFieldsIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  MoneyOff as MoneyOffIcon,
  Gavel as GavelIcon,
  FilterAlt,
  PointOfSale as PointOfSaleIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useProjectExportService } from "../../../../../use-cases/project-export/useProjectExportService";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../application/states/hooks";
import { clearProjectExportContentState } from "../../../../../application/states/project-export/project-export-slice";
import {
  IProjectExportContent,
  IFilter,
} from "../../../../../application/models/IProjectExportContent";
import { ICepList, IProjectExport } from "../../../../../application/models";
import { IEconomicActivityList } from "../../../../../application/models";
import { formatCnpj, numericCnpj } from "../../../../../utils";
import { useBlackListApi } from "../../../../../infra/black-list-api";
import { TransitionProps } from "@mui/material/transitions";
import { useCepListService } from "../../../../../use-cases/cep-list/useCepListService";
import { useMemo } from "react";
import { FixedSizeList as VirtualList } from "react-window";
import { executeAlert } from "../../../../../application/states/toast-alert";
import { ToastAlert } from "../../../../toast-alert";
import { ICompareExportContentWithBlacklistResponse } from "../../../../../application/models/ICompareExportContentWithBlacklistResponse";
import { useBlackListService } from "../../../../../use-cases/black-list/useBlackListService";
import { useEconomicActivityListService } from "../../../../../use-cases/economic-activity-list/useEconomicActivityListService";

interface Props {
  projectExport: IProjectExport;
  onOpenExport?: (id: string) => void;
  isInAccordion?: boolean;
  inModal?: boolean;
}

const formatNumber = (value?: number) =>
  value !== undefined && value !== null ? value.toLocaleString("pt-BR") : "—";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const ProjectExportDetailsRenderer: React.FC<Props> = ({
  projectExport,
  onOpenExport,
  isInAccordion = false,
  inModal,
}) => {
  const dispatch = useAppDispatch();
  const { loadProjectExportContent, compareExportContentWithBlacklist } =
    useProjectExportService();
  const content = useAppSelector((state) => state.projectExport.content) as
    | IProjectExportContent
    | undefined;
  const user = useAppSelector((state) => state.user);
  const { findBlackListByIdApi } = useBlackListApi();
  const { findByIdCepList } = useCepListService();
  const { findEconomicActivityListById } = useEconomicActivityListService();
  const { selectBlackListToEdit } = useBlackListService();
  const [cepListsDetails, setCepListsDetails] = useState<
    Record<string, ICepList>
  >({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [creatorName] = useState(projectExport.userName);
  const [blackListMap, setBlackListMap] = useState<Record<string, string>>({});
  const [compareResult, setCompareResult] =
    useState<ICompareExportContentWithBlacklistResponse | null>(null);
  const [compareLoading, setCompareLoading] = useState<string | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [searchCnpj, setSearchCnpj] = useState<string>("");
  const [showDePara, setShowDePara] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [blacklistData, setBlacklistData] = useState<Record<string, any>>({});
  const [blacklistSearchTerms, setBlacklistSearchTerms] = useState<
    Record<string, string>
  >({});
  const [blacklistSearchTermsDebounced, setBlacklistSearchTermsDebounced] =
    useState<Record<string, string>>({});
  const [economicActivityListsDetails, setEconomicActivityListsDetails] =
    useState<Record<string, any>>({});
  const [deduplicationAccordionExpanded, setDeduplicationAccordionExpanded] =
    useState(false);
  const [exportListSearchTerm, setExportListSearchTerm] = useState<string>("");
  const [exportListSearchTermDebounced, setExportListSearchTermDebounced] =
    useState<string>("");
  const [isComparing, setIsComparing] = useState(false);

  const allowedUserTypes = [
    "OWNER",
    "ADMIN",
    "CUSTOMER EXPERIENCE",
    "SALES SUPERVISOR",
    "SELLER",
    "OPERATOR",
  ];
  const canSeeExportAndBlacklist = allowedUserTypes.includes(
    user.userType as string,
  );
  const selectedCepList = useAppSelector(
    (state) => state.cepList.selectedCepList,
  );

  const getFilterIcon = (f: IFilter) => {
    if ((f.termsFilter?.length ?? 0) > 0)
      return <TextFieldsIcon fontSize="small" />;
    if ((f.notIncludeTermsFilter?.length ?? 0) > 0)
      return <ContentPasteOffIcon fontSize="small" />;
    if (f.field && f.value) return <FindInPageIcon fontSize="small" />;
    if ((f.economicGroup?.length ?? 0) > 0)
      return <FactoryIcon fontSize="small" />;
    if ((f.status?.length ?? 0) > 0) return <GradingIcon fontSize="small" />;
    if ((f.specialStatus?.length ?? 0) > 0)
      return <FindInPageIcon fontSize="small" />;
    if (f.justPrimaryActivity != null) return <FactoryIcon fontSize="small" />;
    if ((f.economicActivity?.length ?? 0) > 0)
      return <ApartmentIcon fontSize="small" />;
    if ((f.economicActivityList?.length ?? 0) > 0)
      return <ApartmentIcon fontSize="small" />;
    if ((f.segmentos?.length ?? 0) > 0) return <GridOnIcon fontSize="small" />;
    if (
      f.staff?.employee &&
      (f.staff.employee.min != null || f.staff.employee.max != null)
    )
      return <Diversity3Icon fontSize="small" />;
    if (
      f.staff?.businessAssociate &&
      (f.staff.businessAssociate.min != null ||
        f.staff.businessAssociate.max != null)
    )
      return <ConnectWithoutContactIcon fontSize="small" />;
    if (
      f.contacts &&
      ((f.contacts.business?.length ?? 0) > 0 ||
        (f.contacts.partner?.length ?? 0) > 0)
    )
      return <LocalPhoneIcon fontSize="small" />;
    if ((f.location?.length ?? 0) > 0)
      return <LocationOnIcon fontSize="small" />;
    if ((f.purgeLocation?.length ?? 0) > 0)
      return <ContentPasteOffIcon fontSize="small" />;
    if ((f.cepLists?.length ?? 0) > 0)
      return <LocationOnIcon fontSize="small" />;
    if ((f.purgeCepLists?.length ?? 0) > 0)
      return <ContentPasteOffIcon fontSize="small" />;
    if ((f.purgeEconomicActivityLists?.length ?? 0) > 0)
      return <ContentPasteOffIcon fontSize="small" />;

    if (f.revenues && (f.revenues.min != null || f.revenues.max != null))
      return <AttachMoneyIcon fontSize="small" />;
    if ((f.includeBusinessSize?.length ?? 0) > 0)
      return <DomainAddIcon fontSize="small" />;
    if ((f.notIncludeBusinessSize?.length ?? 0) > 0)
      return <ContentPasteOffIcon fontSize="small" />;
    if (
      f.shareCapital &&
      (f.shareCapital.min != null || f.shareCapital.max != null)
    )
      return <CurrencyExchangeIcon fontSize="small" />;
    if (f.includeActiveDebts === true) return <MoneyOffIcon fontSize="small" />;
    if ((f.debts?.debt?.length ?? 0) > 0)
      return <MoneyOffIcon fontSize="small" />;
    if ((f.creditScoreDescriptions?.length ?? 0) > 0)
      return <GradingIcon fontSize="small" />;
    if (f.useGeolocationFilter != null)
      return <TravelExploreIcon fontSize="small" />;
    if (f.cepFilterByLatLong) return <LocationOnIcon fontSize="small" />;
    if (f.filterByLatLong != null)
      return <TravelExploreIcon fontSize="small" />;
    if (f.geolocation) return <TravelExploreIcon fontSize="small" />;
    if (f.radius != null) return <TravelExploreIcon fontSize="small" />;
    if (f.createdAt?.min != null || f.createdAt?.max != null)
      return <CalendarMonthIcon fontSize="small" />;
    if (
      (f.naturezaJuridica?.include?.length ?? 0) > 0 ||
      (f.naturezaJuridica?.notInclude?.length ?? 0) > 0
    )
      return <GavelIcon fontSize="small" />;
    if ((f.blackLists?.length ?? 0) > 0)
      return <DeviceHubIcon fontSize="small" />;
    if ((f.sector?.length ?? 0) > 0) return <FactoryIcon fontSize="small" />;
    if ((f.regimeTributario?.length ?? 0) > 0)
      return <PointOfSaleIcon fontSize="small" />;
    return null;
  };

  // Carrega conteúdo
  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    loadProjectExportContent(projectExport.id);
  };

  const handleClose = () => {
    setAnchorEl(null);
    dispatch(clearProjectExportContentState());
    setBlackListMap({});
  };

  const open = Boolean(anchorEl);
  const isLoaded = content?.id === projectExport.id;

  // Busca nomes e dados de blacklists
  useEffect(() => {
    if (!isLoaded || !content) return;
    const ids = Array.from(
      new Set(content.filter.flatMap((f) => f.blackLists ?? [])),
    );
    Promise.all(
      ids.map((id) =>
        findBlackListByIdApi({ blackListId: id })
          .then((bl) => ({ id, name: bl.name, data: bl }))
          .catch(() => ({ id, name: id, data: null })),
      ),
    ).then((results) => {
      const map: Record<string, string> = {};
      const dataMap: Record<string, any> = {};
      results.forEach((r) => {
        map[r.id] = r.name;
        if (r.data) {
          dataMap[r.id] = r.data;
        }
      });
      setBlackListMap(map);
      setBlacklistData(dataMap);
    });
  }, [isLoaded, content, findBlackListByIdApi]);

  // Função para carregar dados das blacklists
  const loadBlacklistData = async (blacklistId: string) => {
    try {
      const blacklist = await findBlackListByIdApi({
        blackListId: blacklistId,
      });
      return blacklist;
    } catch (error) {
      return null;
    }
  };
  useEffect(() => {
    if (!isLoaded || !content) return;
    if (!content.filter) return;

    content.filter.forEach((f) => {
      // Busca para listas de inclusão
      (f.cepLists ?? []).forEach((id) => {
        if (!cepListsDetails[id]) {
          findByIdCepList({ cepListId: id }).then((cepList: any) => {
            setCepListsDetails((prev) => ({
              ...prev,
              [id]: cepList || [],
            }));
          });
        }
      });
      // Busca para listas de exclusão
      (f.purgeCepLists ?? []).forEach((id) => {
        if (!cepListsDetails[id]) {
          findByIdCepList({ cepListId: id }).then((cepList: any) => {
            setCepListsDetails((prev) => ({
              ...prev,
              [id]: cepList || [],
            }));
          });
        }
      });

      // NOVO: Carregar Economic Activity Lists
      (f.economicActivityList ?? []).forEach(
        (economicActivityList: IEconomicActivityList) => {
          if (!economicActivityListsDetails[economicActivityList.id]) {
            const idList = economicActivityList.id;
            findEconomicActivityListById(idList).then((list: any) => {
              setEconomicActivityListsDetails((prev) => ({
                ...prev,
                [idList]: list || [],
              }));
            });
          }
        },
      );

      (f.purgeEconomicActivityLists ?? []).forEach(
        (economicActivityList: IEconomicActivityList) => {
          if (!economicActivityListsDetails[economicActivityList.id]) {
            const idList = economicActivityList.id;
            findEconomicActivityListById(idList).then((list: any) => {
              setEconomicActivityListsDetails((prev) => ({
                ...prev,
                [idList]: list || [],
              }));
            });
          }
        },
      );
    });
  }, [isLoaded, content, findByIdCepList]);

  // Debounce para busca das blacklists
  useEffect(() => {
    const timer = setTimeout(() => {
      setBlacklistSearchTermsDebounced(blacklistSearchTerms);
    }, 200); // 30 delay

    return () => clearTimeout(timer);
  }, [blacklistSearchTerms]);

  // Debounce para busca da lista da extração
  useEffect(() => {
    const timer = setTimeout(() => {
      setExportListSearchTermDebounced(exportListSearchTerm);
    }, 200); // 30 delay

    return () => clearTimeout(timer);
  }, [exportListSearchTerm]);

  // Memoização das listas filtradas das blacklists
  const memoizedFilteredLists = useMemo(() => {
    const result: Record<string, string[]> = {};

    Object.keys(blacklistData).forEach((id) => {
      const searchTerm = blacklistSearchTermsDebounced[id];
      const list = blacklistData[id]?.list || [];

      if (!searchTerm) {
        result[id] = list;
      } else {
        result[id] = list.filter((cnpj: string) =>
          numericCnpj(cnpj).includes(numericCnpj(searchTerm)),
        );
      }
    });

    return result;
  }, [blacklistData, blacklistSearchTermsDebounced]);

  // Memoização da lista da extração filtrada
  const memoizedExportList = useMemo(() => {
    if (!content?.idList) return [];
    if (!exportListSearchTermDebounced) {
      return content.idList;
    }

    return content.idList.filter((cnpj: string) =>
      numericCnpj(cnpj).includes(numericCnpj(exportListSearchTermDebounced)),
    );
  }, [content?.idList, exportListSearchTermDebounced]);

  const filteredDuplicated = useMemo(() => {
    if (!compareResult) return [];
    const allDuplicated = compareResult.blacklists.flatMap(
      (bl) => bl.duplicated,
    );
    return searchCnpj
      ? allDuplicated.filter((item: string) =>
          numericCnpj(item).includes(numericCnpj(searchCnpj)),
        )
      : allDuplicated;
  }, [compareResult, searchCnpj]);

  const filteredExportList = useMemo(() => {
    if (!compareResult) return [];
    return searchCnpj
      ? compareResult.exportList.filter((item: string) =>
          numericCnpj(item).includes(numericCnpj(searchCnpj)),
        )
      : compareResult.exportList;
  }, [compareResult, searchCnpj]);

  const filteredBlacklist = useMemo(() => {
    if (!compareResult) return [];
    const allBlacklist = compareResult.blacklists.flatMap((bl) => bl.list);
    return searchCnpj
      ? allBlacklist.filter((item: string) =>
          numericCnpj(item).includes(numericCnpj(searchCnpj)),
        )
      : allBlacklist;
  }, [compareResult, searchCnpj]);

  // Toast para duplicados
  useEffect(() => {
    if (compareResult && compareResult.blacklists) {
      const allDuplicated = compareResult.blacklists.flatMap(
        (bl) => bl.duplicated,
      );
      if (allDuplicated.length > 0) {
        dispatch(
          executeAlert({
            message: "CNPJ duplicado encontrado!",
            type: "warning",
          }),
        );
      } else {
        dispatch(
          executeAlert({
            message: "Não foi encontrado CNPJs duplicados.",
            type: "success",
          }),
        );
      }
    }
  }, [compareResult, dispatch]);

  // Memo para duplicados (Set para busca O(1))
  const duplicatedSet = useMemo(() => {
    if (!compareResult) return new Set();
    return new Set(compareResult.blacklists.flatMap((bl) => bl.duplicated));
  }, [compareResult]);

  // Memo para listas ordenadas por duplicidade
  const sortedBlacklistData = useMemo(() => {
    const result: Record<string, string[]> = {};
    Object.keys(memoizedFilteredLists).forEach((id) => {
      const list = memoizedFilteredLists[id];
      result[id] = [...list].sort((a, b) => {
        const aIsDuplicated = duplicatedSet.has(a);
        const bIsDuplicated = duplicatedSet.has(b);
        if (aIsDuplicated && !bIsDuplicated) return -1;
        if (!aIsDuplicated && bIsDuplicated) return 1;
        return 0;
      });
    });
    return result;
  }, [memoizedFilteredLists, duplicatedSet]);

  // Renderiza lista de filtros
  const renderFilters = (filters: IFilter[]): ReactNode => (
    <Box sx={{ width: "100%" }}>
      {filters.map((f, i) => (
        <Box
          key={i}
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            bgcolor: "#fafafa",
            width: "100%",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filtros
          </Typography>
          <List dense disablePadding>
            {(f.location?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LOCALIZAÇÃO / UF / CIDADE:</strong>
                      <br />
                      {f.location?.map((l, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {l.value}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.purgeLocation?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LOCALIZAÇÃO EXCLUÍDA:</strong>
                      <br />
                      {f.purgeLocation?.map((l, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {l.value}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.cepLists?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LISTAS DE CEP:</strong>
                      {f.cepLists?.map((id) => (
                        <Box key={id} sx={{ ml: 1, display: "block" }}>
                          <Typography variant="subtitle2" color="primary">
                            {cepListsDetails[id]?.name || "Carregando..."}
                          </Typography>
                          <Box sx={{ ml: 2 }}>
                            {cepListsDetails[id]?.list ? (
                              cepListsDetails[id].list.map(
                                (cep: string, idx: number) => (
                                  <span key={idx} style={{ display: "block" }}>
                                    ° {cep}
                                  </span>
                                ),
                              )
                            ) : (
                              <Typography variant="body2">
                                Carregando...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.purgeCepLists?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>CEPS EXCLUÍDOS:</strong>
                      {f.purgeCepLists?.map((id) => (
                        <Box key={id} sx={{ ml: 1, display: "block" }}>
                          <Typography variant="subtitle2" color="primary">
                            {cepListsDetails[id]?.name || "Carregando..."}
                          </Typography>
                          <Box sx={{ ml: 2 }}>
                            {cepListsDetails[id]?.list ? (
                              cepListsDetails[id].list.map(
                                (cep: string, idx: number) => (
                                  <span key={idx} style={{ display: "block" }}>
                                    ° {cep}
                                  </span>
                                ),
                              )
                            ) : (
                              <Typography variant="body2">
                                Carregando...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {/* LISTAS DE ATIVIDADE ECONÔMICA */}
            {(f.economicActivityList?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LISTAS DE ATIVIDADE ECONÔMICA:</strong>
                      {f.economicActivityList?.map(
                        (item: IEconomicActivityList) => (
                          <Box key={item.id} sx={{ ml: 1, display: "block" }}>
                            <Typography variant="subtitle2" color="primary">
                              {economicActivityListsDetails[item.id]?.name ||
                                "Carregando... "}
                            </Typography>
                            <Box sx={{ ml: 2 }}>
                              {economicActivityListsDetails[item.id]?.list ? (
                                economicActivityListsDetails[item.id].list.map(
                                  (cnae: any, idx: number) => (
                                    <span
                                      key={idx}
                                      style={{ display: "block" }}
                                    >
                                      °{" "}
                                      {cnae.cnaeDescricao ||
                                        cnae.codigo ||
                                        cnae}
                                    </span>
                                  ),
                                )
                              ) : (
                                <Typography variant="body2"></Typography>
                              )}
                            </Box>
                          </Box>
                        ),
                      )}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.purgeEconomicActivityLists?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ContentPasteOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>
                        LISTAS DE ATIVIDADE ECONÔMICA (DEDUPLICAÇÃO):
                      </strong>
                      {f.purgeEconomicActivityLists?.map(
                        (item: IEconomicActivityList) => (
                          <Box key={item.id} sx={{ ml: 1, display: "block" }}>
                            <Typography variant="subtitle2" color="primary">
                              {economicActivityListsDetails[item.id]?.name ||
                                "Carregando..."}
                            </Typography>
                            <Box sx={{ ml: 2 }}>
                              {economicActivityListsDetails[item.id]?.list ? (
                                economicActivityListsDetails[item.id].list.map(
                                  (cnae: any, idx: number) => (
                                    <span
                                      key={idx}
                                      style={{ display: "block" }}
                                    >
                                      °{" "}
                                      {cnae.cnaeDescricao ||
                                        cnae.codigo ||
                                        cnae}
                                    </span>
                                  ),
                                )
                              ) : (
                                <Typography variant="body2"></Typography>
                              )}
                            </Box>
                          </Box>
                        ),
                      )}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.useGeolocationFilter != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>FILTRO DE GEOLOCALIZAÇÃO:</strong>{" "}
                      {f.useGeolocationFilter ? "Sim" : "Não"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.geolocation && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>GEOLOCALIZAÇÃO:</strong> lat {f.geolocation.lat},
                      lon {f.geolocation.lon}, raio {f.geolocation.radius}{" "}
                      km{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.filterByLatLong != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>FILTRAR POR LATITUDE/LONGITUDE:</strong>{" "}
                      {f.filterByLatLong ? "Sim" : "Não"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.cepFilterByLatLong && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>CEPS PARA GEOLOCALIZAÇÃO:</strong>{" "}
                      {f.cepFilterByLatLong}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.radius != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>RAIO (km):</strong> {f.radius}{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.sector?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <FactoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SETOR ECONÔMICO:</strong>
                      <br />
                      {f.sector?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.segmentos?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <GridOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SEGMENTOS / GRUPOS:</strong>
                      <br />
                      {f.segmentos?.map((s, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {s.segmento}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.justPrimaryActivity != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>INCLUIR APENAS ATIVIDADE PRIMÁRIA:</strong>{" "}
                      {f.justPrimaryActivity ? "SIM" : "NÃO"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.economicActivity?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>ATIVIDADE ECONÔMICA / CNAE:</strong>
                      <br />
                      {f.economicActivity?.map((a, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {a.cnaeDescricao}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.includeBusinessSize?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <DomainAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PORTE DA EMPRESA:</strong>
                      <br />
                      {f.includeBusinessSize?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.notIncludeBusinessSize?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <DomainAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PORTE DA EMPRESA EXCLUÍDA:</strong>
                      <br />
                      {f.notIncludeBusinessSize?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.revenues &&
              (f.revenues.min != null || f.revenues.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <AttachMoneyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <strong>FATURAMENTO:</strong>{" "}
                        {f.revenues.min != null
                          ? `R$ ${formatNumber(f.revenues.min)}`
                          : "—"}{" "}
                        a{" "}
                        {f.revenues.max != null
                          ? `R$ ${formatNumber(f.revenues.max)}`
                          : "—"}
                      </>
                    }
                  />
                </ListItem>
              )}
            {f.staff?.employee &&
              (f.staff.employee.min != null ||
                f.staff.employee.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <Diversity3Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        {" "}
                        <strong>NÚMERO DE FUNCIONÁRIOS:</strong>{" "}
                        {formatNumber(f.staff.employee.min)} a{" "}
                        {formatNumber(f.staff.employee.max)}{" "}
                      </>
                    }
                  />
                </ListItem>
              )}
            {f.staff?.businessAssociate &&
              (f.staff.businessAssociate.min != null ||
                f.staff.businessAssociate.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <ConnectWithoutContactIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        {" "}
                        <strong>NÚMERO DE SÓCIOS:</strong>{" "}
                        {formatNumber(f.staff.businessAssociate.min)} a{" "}
                        {formatNumber(f.staff.businessAssociate.max)}{" "}
                      </>
                    }
                  />
                </ListItem>
              )}
            {f.shareCapital &&
              (f.shareCapital.min != null || f.shareCapital.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <CurrencyExchangeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <strong>CAPITAL SOCIAL:</strong>{" "}
                        {f.shareCapital.min != null
                          ? `R$ ${formatNumber(f.shareCapital.min)}`
                          : "—"}{" "}
                        a{" "}
                        {f.shareCapital.max != null
                          ? `R$ ${formatNumber(f.shareCapital.max)}`
                          : "—"}
                      </>
                    }
                  />
                </ListItem>
              )}
            {(f.economicGroup?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <DeviceHubIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>MATRIZ / FILIAL:</strong>
                      <br />
                      {f.economicGroup?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.status?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <GradingIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SITUAÇÃO CADASTRAL:</strong>
                      <br />
                      {f.status?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.createdAt?.min != null || f.createdAt?.max != null) && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <CalendarMonthIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>DATA DE ABERTURA:</strong>{" "}
                      {f.createdAt.min
                        ? new Date(f.createdAt.min).toLocaleDateString()
                        : "—"}{" "}
                      e{" "}
                      {f.createdAt.max
                        ? new Date(f.createdAt.max).toLocaleDateString()
                        : "—"}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.termsFilter?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TextFieldsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PALAVRAS CHAVE:</strong>
                      <br />
                      {f.termsFilter?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.notIncludeTermsFilter?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TextFieldsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PALAVRAS CHAVE EXCLUÍDAS:</strong>
                      <br />
                      {f.notIncludeTermsFilter?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.creditScoreDescriptions?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <MoneyOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>
                        PENDÊNCIAS FINANCEIRAS / Saúde empresarial:
                      </strong>
                      <br />
                      {f.creditScoreDescriptions?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.includeActiveDebts === true && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <MoneyOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>
                        PENDÊNCIAS FINANCEIRAS / Dívida Ativa:
                      </strong>{" "}
                      {f.includeActiveDebts === true ? "Sim" : "Não"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.debts?.debt?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <MoneyOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PENDÊNCIAS FINANCEIRAS:</strong>
                      <br />
                      {f.debts?.debt?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.field && f.value && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <FindInPageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>CAMPO PERSONALIZADO:</strong> {f.field}:{" "}
                      {f.value}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.specialStatus?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <FindInPageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SITUAÇÃO ESPECIAL:</strong>{" "}
                      {f.specialStatus?.includes("NOT_INCLUDE")
                        ? "Não incluso"
                        : f.specialStatus?.join(", ")}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.regimeTributario?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <PointOfSaleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>REGIME TRIBUTÁRIO:</strong>
                      <br />
                      {f.regimeTributario?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {((f.naturezaJuridica?.include?.length ?? 0) > 0 ||
              (f.naturezaJuridica?.notInclude?.length ?? 0) > 0) && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <GavelIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {(f.naturezaJuridica?.include?.length ?? 0) > 0 && (
                        <>
                          <strong>NATUREZA JURÍDICA (incluir):</strong>
                          <br />
                          {f.naturezaJuridica?.include?.map((n, idx) => (
                            <span key={idx} style={{ display: "block" }}>
                              ° {n.descricao}
                            </span>
                          ))}
                          <br />
                        </>
                      )}
                      {(f.naturezaJuridica?.notInclude?.length ?? 0) > 0 && (
                        <>
                          <strong>NATUREZA JURÍDICA (excluir):</strong>
                          <br />
                          {f.naturezaJuridica?.notInclude?.map((n, idx) => (
                            <span key={idx} style={{ display: "block" }}>
                              ° {n.descricao}
                            </span>
                          ))}
                        </>
                      )}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.contacts && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocalPhoneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>CONTATOS (Empresas):</strong>
                      <br />
                      {(f.contacts.business ?? []).map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                      <br />
                      <strong>CONTATOS (Sócios):</strong>
                      <br />
                      {(f.contacts.partner ?? []).map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {/* { f.justPrimaryActivity != null && (
              <ListItem disableGutters>
                <ListItemIcon><FactoryIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={<> <strong>ATIVIDADE ECONÔMICA:</strong> {f.justPrimaryActivity ? 'Sim' : 'Não'} </>}
                />
              </ListItem>
            ) } */}

            {(f.blackLists?.length ?? 0) > 0 && (
              <ListItem
                disableGutters
                sx={{ flexDirection: "column", alignItems: "flex-start" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    width: "100%",
                  }}
                >
                  <ListItemIcon>
                    <ContentPasteOffIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                    DEDUPLICAÇÃO:
                  </Typography>
                </Box>
                {/* Dropdown com as listas de deduplicação */}
                <Accordion
                  expanded={deduplicationAccordionExpanded}
                  onChange={() =>
                    setDeduplicationAccordionExpanded(
                      !deduplicationAccordionExpanded,
                    )
                  }
                  sx={{
                    width: "100%",
                    bgcolor: "#fafafa",
                    boxShadow: "none",
                    border: "1px solid #eee",
                    mb: 1,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Listas de Deduplicação
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Lista das blacklists com seus CNPJs em tabelas lado a lado */}
                    <Grid container spacing={2}>
                      {Array.from(new Set(f.blackLists)).map((id, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          {blacklistData[id] && blacklistData[id].list && (
                            <Box
                              sx={{
                                bgcolor: "#f9f9f9",
                                boxShadow: "none",
                                border: "1px solid #eee",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 0.5,
                                  p: 1,
                                  bgcolor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                {blackListMap[id] ?? id}: (
                                {blacklistData[id].list.length.toLocaleString(
                                  "pt-BR",
                                )}
                                )
                                {compareResult &&
                                  (() => {
                                    const duplicatedCount = blacklistData[
                                      id
                                    ].list.filter((cnpj: string) =>
                                      compareResult.blacklists.some((bl) =>
                                        bl.duplicated.includes(cnpj),
                                      ),
                                    ).length;
                                    return duplicatedCount > 0
                                      ? ` - ${duplicatedCount.toLocaleString("pt-BR")} duplicado${duplicatedCount > 1 ? "s" : ""}`
                                      : "";
                                  })()}
                              </Typography>
                              <TextField
                                size="small"
                                placeholder="Buscar CNPJ"
                                value={blacklistSearchTerms[id] || ""}
                                onChange={(e) =>
                                  setBlacklistSearchTerms((prev) => ({
                                    ...prev,
                                    [id]: e.target.value,
                                  }))
                                }
                                sx={{
                                  width: "50%",
                                  minWidth: 120,
                                  maxWidth: 220,
                                  mb: 1,
                                  px: 1,
                                }}
                                inputProps={{ maxLength: 18 }}
                              />
                              {!canSeeExportAndBlacklist ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    my: 2,
                                    textAlign: "center",
                                  }}
                                >
                                  Visualização restrita
                                </Typography>
                              ) : isComparing ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: 200,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Comparando CNPJs...
                                  </Typography>
                                </Box>
                              ) : blacklistData[id].list.length > 0 ? (
                                <VirtualList
                                  height={340}
                                  itemCount={
                                    memoizedFilteredLists[id]?.length || 0
                                  }
                                  itemSize={44}
                                  width="100%"
                                >
                                  {({ index, style }) => {
                                    const sortedList =
                                      sortedBlacklistData[id] || [];

                                    // Checa se existe resultado de comparação e se o CNPJ está duplicado nesta blacklist
                                    const isDuplicated = duplicatedSet.has(
                                      sortedList[index],
                                    );

                                    return (
                                      <ListItem
                                        style={style}
                                        key={sortedList[index] + index}
                                        sx={{
                                          py: 0.5,
                                          bgcolor: isDuplicated
                                            ? "#ffcccc"
                                            : index % 2 === 0
                                              ? "#f5f5f5"
                                              : "#e0e0e0",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <ListItemText
                                          primary={formatCnpj(
                                            sortedList[index],
                                          )}
                                        />
                                      </ListItem>
                                    );
                                  }}
                                </VirtualList>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    my: 2,
                                    textAlign: "center",
                                  }}
                                >
                                  Nenhum encontrado
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Grid>
                      ))}
                    </Grid>
                    {/* Botão De/Para dentro do dropdown */}
                    {canSeeExportAndBlacklist && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (showDePara) {
                              // Se está fechando, fecha o Accordion também
                              setDeduplicationAccordionExpanded(false);
                            }
                            setShowDePara((prev) => !prev);
                            if (!showDePara && !compareResult) {
                              setIsComparing(true);
                              setCompareLoading("all");
                              compareExportContentWithBlacklist(
                                projectExport.id,
                              )
                                .then((result) => {
                                  setCompareResult(result);
                                })
                                .catch((err) =>
                                  setCompareError(
                                    err.message || "Erro ao comparar",
                                  ),
                                )
                                .finally(() => {
                                  setCompareLoading(null);
                                  // Aguarda um pouco antes de remover o loading das tabelas
                                  setTimeout(() => {
                                    setIsComparing(false);
                                  }, 500); // 500ms de delay
                                });
                            }
                          }}
                          disabled={compareLoading === "all"}
                        >
                          {compareLoading === "all"
                            ? "Comparando..."
                            : showDePara
                              ? "Fechar listas de deduplicação"
                              : "Verificar CNPJs duplicados"}
                          {compareResult &&
                            (() => {
                              const totalDuplicated =
                                compareResult.blacklists.flatMap(
                                  (bl) => bl.duplicated,
                                ).length;
                              return totalDuplicated > 0
                                ? ` (${totalDuplicated.toLocaleString("pt-BR")} duplicado${totalDuplicated > 1 ? "s" : ""})`
                                : "";
                            })()}
                        </Button>
                      </Box>
                    )}
                    {/* Dropdown De/Para dentro do dropdown */}
                    {showDePara && compareResult && false && (
                      <Accordion
                        expanded
                        sx={{
                          width: "100%",
                          bgcolor: "#f9f9f9",
                          boxShadow: "none",
                          border: "1px solid #eee",
                          mb: 1,
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          onClick={() => setShowDePara(false)}
                        >
                          <Typography variant="subtitle2">
                            Resultado De/Para
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TextField
                            size="small"
                            placeholder="Buscar CNPJ"
                            value={searchCnpj}
                            onChange={(e) => setSearchCnpj(e.target.value)}
                            sx={{
                              width: "50%",
                              minWidth: 120,
                              maxWidth: 220,
                              mb: 2,
                            }}
                            inputProps={{ maxLength: 18 }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {searchCnpj && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchCnpj("");
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {compareLoading === "all" && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Carregando...
                            </Typography>
                          )}
                          {compareError && (
                            <Typography color="error" sx={{ mb: 1 }}>
                              {compareError}
                            </Typography>
                          )}
                          <Box display="flex" gap={2}>
                            {/* Coluna 1: Lista de deduplicação */}
                            <Box flex={1}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, mb: 0.5 }}
                              >
                                Lista de deduplicação (
                                {filteredBlacklist.length})
                              </Typography>
                              <VirtualList
                                height={240}
                                itemCount={filteredBlacklist.length}
                                itemSize={44}
                                width="100%"
                              >
                                {({ index, style }) => {
                                  const cnpj = filteredBlacklist[index];
                                  const isDuplicated =
                                    filteredDuplicated.includes(cnpj);
                                  return (
                                    <ListItem
                                      style={style}
                                      key={cnpj + index}
                                      sx={{
                                        py: 0.5,
                                        bgcolor: isDuplicated
                                          ? "#ffcccc"
                                          : index % 2 === 0
                                            ? "#f5f5f5"
                                            : "#e0e0e0",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <ListItemText
                                        primary={formatCnpj(cnpj)}
                                      />
                                    </ListItem>
                                  );
                                }}
                              </VirtualList>
                            </Box>
                            {/* Coluna 2: Duplicados */}
                            <Box flex={1}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, mb: 0.5 }}
                              >
                                Duplicados ({filteredDuplicated.length})
                              </Typography>
                              <VirtualList
                                height={240}
                                itemCount={filteredDuplicated.length}
                                itemSize={44}
                                width="100%"
                              >
                                {({ index, style }) => {
                                  const cnpj = filteredDuplicated[index];
                                  return (
                                    <ListItem
                                      style={style}
                                      key={cnpj + index}
                                      sx={{
                                        py: 0.5,
                                        bgcolor: "#ffcccc",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <ListItemText
                                        primary={formatCnpj(cnpj)}
                                      />
                                    </ListItem>
                                  );
                                }}
                              </VirtualList>
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </AccordionDetails>
                </Accordion>
              </ListItem>
            )}
          </List>
        </Box>
      ))}
    </Box>
  );

  // Renderiza o conteúdo de detalhes
  const renderDetailsContent = () => (
    <Box sx={{ width: "100%" }}>
      <List dense sx={{ width: "100%" }}>
        <ListItem>
          <ListItemText primary="Nome" secondary={projectExport.name} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Criado por" secondary={creatorName} />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Data de criação"
            secondary={new Date(projectExport.createdAt).toLocaleString()}
          />
        </ListItem>
        <ListItem alignItems="flex-start">
          <ListItemText
            primary=""
            secondary={
              content?.filter?.length! > 0
                ? renderFilters(content!.filter)
                : "Nenhum filtro"
            }
          />
        </ListItem>
        {compareError && (
          <Typography color="error" sx={{ mt: 1 }}>
            {compareError}
          </Typography>
        )}
        {canSeeExportAndBlacklist &&
          content &&
          content.idList &&
          content.idList.length > 0 && (
            <Accordion
              sx={{
                mt: 2,
                bgcolor: "#fafafa",
                border: "1px solid #eee",
                borderRadius: 1,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Lista da Extração (
                  {content.idList.length.toLocaleString("pt-BR")})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  size="small"
                  placeholder="Buscar CNPJ"
                  value={exportListSearchTerm}
                  onChange={(e) => setExportListSearchTerm(e.target.value)}
                  sx={{ width: "50%", minWidth: 120, maxWidth: 220, mb: 1 }}
                  inputProps={{ maxLength: 18 }}
                />
                {content.idList.length > 0 ? (
                  <VirtualList
                    height={240}
                    itemCount={memoizedExportList.length}
                    itemSize={44}
                    width="100%"
                  >
                    {({ index, style }) => {
                      const cnpj = memoizedExportList[index];
                      return (
                        <ListItem
                          style={style}
                          key={cnpj + index}
                          sx={{
                            py: 0.5,
                            bgcolor: index % 2 === 0 ? "#f5f5f5" : "#e0e0e0",
                            borderRadius: 1,
                          }}
                        >
                          <ListItemText primary={formatCnpj(cnpj)} />
                        </ListItem>
                      );
                    }}
                  </VirtualList>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", my: 2, textAlign: "center" }}
                  >
                    Nenhum encontrado
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}
      </List>
    </Box>
  );

  // 1) Se for usado dentro de Accordion, só mostra a List de detalhes
  if (isInAccordion) {
    if (!isLoaded) return <Typography>Carregando…</Typography>;
    return renderDetailsContent();
  }

  // Se estiver no modal (ex: inModal === true)
  if (inModal) {
    if (!isLoaded) return <Typography>Carregando…</Typography>;
    return renderDetailsContent();
  }

  // 2) Caso contrário, renderiza o botão + Dialog (fullScreen)
  return (
    <Box display="flex" alignItems="center">
      <Typography
        variant="body2"
        onClick={() => onOpenExport?.(projectExport.id)}
        sx={{ cursor: onOpenExport ? "pointer" : "default" }}
      >
        {projectExport.name}
      </Typography>
      <IconButton size="small" onClick={handleOpen} sx={{ ml: 0.5 }}>
        <ExpandMoreIcon fontSize="small" />
      </IconButton>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Detalhes da Extração
            </Typography>
            <Button autoFocus color="inherit" onClick={handleClose}>
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            p: 4,
            maxWidth: "100vw",
            maxHeight: "100vh",
            overflowY: "auto",
            bgcolor: "background.paper",
          }}
        >
          {!isLoaded ? (
            <Typography>Carregando…</Typography>
          ) : (
            renderDetailsContent()
          )}
        </Box>
      </Dialog>
    </Box>
  );
};
